/*
 * @license
 * Copyright Endlessjs. All Rights Reserved.
 * Licensed under the MIT License. See License.txt in the project root for license information.
 */

import { Injectable } from '@angular/core';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { ElCollectionViewer } from '../../cdk/collections/collection-viewer';
import { ElDataSource } from '../../cdk/table/data-source';
import { ElSortable, ElSortRequest } from '../tree-grid-sort.component';
import { ElTreeGridDataService } from './tree-grid-data.service';
import { ElTreeGridFilterService } from './tree-grid-filter.service';
import { ElTreeGridSortService } from './tree-grid-sort.service';
import { ElGetters, EL_DEFAULT_ROW_LEVEL, ElTreeGridPresentationNode } from './tree-grid.model';
import { ElToggleOptions, ElTreeGridService } from './tree-grid.service';

export interface ElFilterable {
  filter(filterRequest: string);
}

export class ElTreeGridDataSource<T> extends ElDataSource<ElTreeGridPresentationNode<T>>
                                     implements ElSortable, ElFilterable {
  /** Stream that emits when a new data array is set on the data source. */
  private data: BehaviorSubject<ElTreeGridPresentationNode<T>[]>;

  /** Stream emitting render data to the table (depends on ordered data changes). */
  private readonly renderData = new BehaviorSubject<ElTreeGridPresentationNode<T>[]>([]);

  private readonly filterRequest = new BehaviorSubject<string>('');

  private readonly sortRequest = new BehaviorSubject<ElSortRequest>(null);

  constructor(private sortService: ElTreeGridSortService<T>,
              private filterService: ElTreeGridFilterService<T>,
              private treeGridService: ElTreeGridService<T>,
              private treeGridDataService: ElTreeGridDataService<T>) {
    super();
  }

  setData<N>(data: N[], customGetters?: ElGetters<N, T>) {
    let presentationData: ElTreeGridPresentationNode<T>[] = [];
    if (data) {
      presentationData = this.treeGridDataService.toPresentationNodes(data, customGetters);
    }

    this.data = new BehaviorSubject(presentationData);
    this.updateChangeSubscription();
  }

  connect(
    collectionViewer: ElCollectionViewer,
  ): Observable<ElTreeGridPresentationNode<T>[] | ReadonlyArray<ElTreeGridPresentationNode<T>>> {
    return this.renderData;
  }

  disconnect(collectionViewer: ElCollectionViewer) {
  }

  expand(row: T) {
    this.treeGridService.expand(this.data.value, row);
    this.data.next(this.data.value);
  }

  collapse(row: T) {
    this.treeGridService.collapse(this.data.value, row);
    this.data.next(this.data.value);
  }

  toggle(row: T, options?: ElToggleOptions) {
    this.treeGridService.toggle(this.data.value, row, options);
    this.data.next(this.data.value);
  }

  toggleByIndex(dataIndex: number, options?: ElToggleOptions) {
    const node: ElTreeGridPresentationNode<T> = this.renderData.value && this.renderData.value[dataIndex];
    if (node) {
      this.toggle(node.data, options);
    }
  }

  getLevel(rowIndex: number): number {
    const row = this.renderData.value[rowIndex];
    return row ? row.level : EL_DEFAULT_ROW_LEVEL;
  }

  sort(sortRequest: ElSortRequest) {
    this.sortRequest.next(sortRequest);
  }

  filter(searchQuery: string) {
    this.filterRequest.next(searchQuery);
  }

  protected updateChangeSubscription() {
    const dataStream = this.data;

    const filteredData = combineLatest(dataStream, this.filterRequest)
      .pipe(
        map(([data]) => this.treeGridDataService.copy(data)),
        map(data => this.filterData(data)),
      );

    const sortedData = combineLatest(filteredData, this.sortRequest)
      .pipe(
        map(([data]) => this.sortData(data)),
      );

    sortedData
      .pipe(
        map((data: ElTreeGridPresentationNode<T>[]) => this.treeGridDataService.flattenExpanded(data)),
      )
      .subscribe((data: ElTreeGridPresentationNode<T>[]) => this.renderData.next(data));
  }

  private filterData(data: ElTreeGridPresentationNode<T>[]): ElTreeGridPresentationNode<T>[] {
    return this.filterService.filter(this.filterRequest.value, data);
  }

  private sortData(data: ElTreeGridPresentationNode<T>[]): ElTreeGridPresentationNode<T>[] {
    return this.sortService.sort(this.sortRequest.value, data);
  }
}

@Injectable()
export class ElTreeGridDataSourceBuilder<T> {
  constructor(private filterService: ElTreeGridFilterService<T>,
              private sortService: ElTreeGridSortService<T>,
              private treeGridService: ElTreeGridService<T>,
              private treeGridDataService: ElTreeGridDataService<T>) {
  }

  create<N>(data: N[], customGetters?: ElGetters<N, T>): ElTreeGridDataSource<T> {
    const dataSource = new ElTreeGridDataSource<T>(
      this.sortService,
      this.filterService,
      this.treeGridService,
      this.treeGridDataService,
    );

    dataSource.setData(data, customGetters);
    return dataSource;
  }
}
